import { Notification } from '../../model/notification/Notification';
import i18n from "../../i18n";

const API_URL = process.env.REACT_APP_NOTIFICATION_SERVICE_BASE_URL || '';

/**
 * Derives the notification-service host root from the GraphQL URL and appends
 * the preview path.  Works for both local dev and nginx-proxied ngrok:
 *   http://localhost:8091/graphql  → http://localhost:8091/preview/email
 *   /api/notification/graphql      → /api/notification/preview/email
 */
function previewEmailUrl(): string {
    return API_URL.replace(/\/graphql$/, '/preview/email');
}

/**
 * POST to the notification-service preview endpoint and return the rendered
 * HTML string.  Throws on any non-2xx response.
 *
 * @param token      Auth0 bearer token.
 * @param businessId Business whose brand (name/color/logo) should be applied
 *                   to the preview.  Pass null to use the default template.
 * @param category   Notification category string (e.g. "APPOINTMENT_CONFIRMED").
 * @param title      Subject / title line passed to the template renderer.
 * @param body       Custom body text the business owner typed into the textarea.
 */
export async function previewEmail(
    token: string,
    businessId: number | null,
    category: string,
    title: string,
    body: string
): Promise<string> {
    const url = previewEmailUrl();
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt",
        },
        body: JSON.stringify({ businessId, category, title, body }),
    });
    if (!res.ok) {
        throw new Error(`Preview request failed: ${res.status} ${res.statusText}`);
    }
    return res.text();
}

/**
 * Fetch the latest notifications for a business.
 * businessId is coerced to Number to avoid the known ID-vs-Int mismatch.
 */
export async function getNotifications(
    businessId: number | string,
    token: string,
    limit?: number
): Promise<Notification[]> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt" },
        body: JSON.stringify({
            query: `query notifications($businessId: ID!, $limit: Int) {
                notifications(businessId: $businessId, limit: $limit) {
                    id businessId title body category referenceId read createdAt
                }
            }`,
            variables: { businessId: Number(businessId), limit: limit ?? null },
        }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e: { message: string }) => e.message).join(', '));
    return json.data.notifications as Notification[];
}

/**
 * Returns the count of unread notifications for the business.
 * businessId is coerced to Number.
 */
export async function getUnreadNotificationCount(
    businessId: number | string,
    token: string
): Promise<number> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt" },
        body: JSON.stringify({
            query: `query unreadNotificationCount($businessId: ID!) {
                unreadNotificationCount(businessId: $businessId)
            }`,
            variables: { businessId: Number(businessId) },
        }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e: { message: string }) => e.message).join(', '));
    return json.data.unreadNotificationCount as number;
}

/** Mark a single notification as read. */
export async function markNotificationRead(
    id: string,
    businessId: number | string,
    token: string
): Promise<boolean> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt" },
        body: JSON.stringify({
            query: `mutation markNotificationRead($id: ID!, $businessId: ID!) {
                markNotificationRead(id: $id, businessId: $businessId)
            }`,
            variables: { id, businessId: Number(businessId) },
        }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e: { message: string }) => e.message).join(', '));
    return json.data.markNotificationRead as boolean;
}

/** Mark all notifications for a business as read. */
export async function markAllNotificationsRead(
    businessId: number | string,
    token: string
): Promise<boolean> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt" },
        body: JSON.stringify({
            query: `mutation markAllNotificationsRead($businessId: ID!) {
                markAllNotificationsRead(businessId: $businessId)
            }`,
            variables: { businessId: Number(businessId) },
        }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e: { message: string }) => e.message).join(', '));
    return json.data.markAllNotificationsRead as boolean;
}
